import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Clock, Phone, Star, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Seo, { orgSchema, breadcrumbSchema } from "@/components/Seo";
import WhatsAppCTA from "@/components/WhatsAppCTA";

const SITE = "https://signiastylettoix.com";

export default function ClinicDetail() {
    const { slug } = useParams();
    const [c, setC] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setC(null);
        api.get(`/clinics/by-slug/${slug}`)
            .then((r) => { if (!cancelled) setC(r.data); })
            .catch(() => { if (!cancelled) setError(true); });
        window.scrollTo(0, 0);
        return () => { cancelled = true; };
    }, [slug]);

    if (error) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Clinic not found. <Link className="underline ml-2" to="/clinics">All clinics</Link></div>;
    if (!c) return <div className="min-h-[60vh] flex items-center justify-center text-[#4a5568]">Loading…</div>;

    const url = `${SITE}/clinics/${c.slug}`;
    const schema = {
        "@context": "https://schema.org",
        "@graph": [orgSchema(),
            breadcrumbSchema([{ name: "Home", url: SITE + "/" }, { name: "Clinics", url: SITE + "/clinics" }, { name: c.name, url }]),
            { "@type": "LocalBusiness", name: c.name, address: { "@type": "PostalAddress", streetAddress: c.address, addressLocality: c.city, addressRegion: c.state, addressCountry: "IN" }, telephone: c.phone, openingHours: c.hours, aggregateRating: c.rating ? { "@type": "AggregateRating", ratingValue: c.rating, reviewCount: 40 } : undefined, url }],
    };

    return (
        <>
            <Seo title={`${c.name} — Signia Hearing Aid Centre in ${c.city}`}
                description={`Visit ${c.name} (${c.address}) for free hearing tests, Signia Styletto trials and expert fitting. ${c.hours}. Book your appointment today.`}
                canonical={url} schema={schema} />
            <section className="pt-12 md:pt-16 pb-16">
                <div className="container-x max-w-[820px]">
                    <nav className="text-[12.5px] text-[#4a5568] mb-6">
                        <Link to="/" className="hover:text-[#003E4D]">Home</Link><span className="mx-2 text-[#9CA3AF]">/</span>
                        <Link to="/clinics" className="hover:text-[#003E4D]">Clinics</Link><span className="mx-2 text-[#9CA3AF]">/</span>
                        <span className="text-[#1a1a1a]">{c.name}</span>
                    </nav>
                    <p className="eyebrow inline-flex items-center gap-2"><MapPin size={12} /> HearClear Clinic · {c.city}</p>
                    <h1 className="font-serif text-[34px] md:text-[46px] leading-[1.1] mt-4" data-testid="clinic-h1">{c.name}</h1>
                    <div className="mt-6 p-6 bg-white border border-[#e2ddd4] rounded-2xl space-y-3 text-[15px]" data-testid="clinic-info">
                        <p className="flex items-center gap-2"><MapPin size={16} className="text-[#003E4D] shrink-0" /> {c.address}</p>
                        <p className="flex items-center gap-2"><Clock size={16} className="text-[#003E4D] shrink-0" /> {c.hours}</p>
                        {c.phone && <p className="flex items-center gap-2"><Phone size={16} className="text-[#003E4D] shrink-0" /> <a className="underline text-[#0F6B7D]" href={`tel:${c.phone.replace(/\s/g, "")}`}>{c.phone}</a></p>}
                        {c.rating && <p className="flex items-center gap-2"><Star size={16} className="text-[#B8860B] shrink-0" /> {c.rating} / 5 patient rating</p>}
                    </div>
                    <div className="mt-6 flex flex-wrap gap-3">
                        <Link to="/book-trial" className="btn-primary" data-testid="clinic-book">Book Appointment Here <ArrowRight size={16} /></Link>
                        <WhatsAppCTA message={`Hi, I'd like to visit ${c.name} for a hearing test.`} sourcePage={url} className="btn-whatsapp">WhatsApp This Clinic</WhatsAppCTA>
                    </div>
                    <div className="mt-10 text-[15.5px] text-[#333] leading-relaxed space-y-3">
                        <h2 className="font-serif text-[26px] text-[#003E4D]">Services at this centre</h2>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>Free clinical hearing test (pure-tone + speech audiometry)</li>
                            <li>Signia Styletto IX / AX demonstrations programmed to your audiogram</li>
                            <li>CROS fitting for single-sided deafness</li>
                            <li>Hearing aid servicing, cleaning and warranty support (any brand assessed)</li>
                            <li>Upgrade-exchange evaluation — up to 45% credit for your old device</li>
                        </ul>
                        <p>Prefer not to travel? <Link to="/book-trial" className="text-[#0F6B7D] underline">Free home visits</Link> are available across {c.city}.</p>
                    </div>
                    {c.other_clinics?.length > 0 && (
                        <div className="mt-12">
                            <h2 className="font-serif text-[24px] text-[#003E4D] mb-4">Other HearClear clinics</h2>
                            <div className="flex flex-wrap gap-2">
                                {c.other_clinics.map((o) => (
                                    <Link key={o.slug} to={`/clinics/${o.slug}`} className="text-[13px] bg-white border border-[#e2ddd4] rounded-full px-4 py-2 text-[#003E4D] hover:border-[#003E4D]">{o.name}</Link>
                                ))}
                                {c.city_slug && <Link to={`/hearing-aids/${c.city_slug}`} className="text-[13px] bg-[#003E4D] text-white rounded-full px-4 py-2">All hearing aids in {c.city}</Link>}
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
}
